import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle,
} from '@mui/material';
import {
  DOWNLOAD_REWARDED_AD_UNIT, TEST_REWARDED_AD_UNIT, requestRewardedAd,
} from '../utils/rewardedAd';
import { recordingDownloadUrl } from '../utils/recordingDownload';
import { API_BASE_URL } from '../utils/api';

const mode = import.meta.env.VITE_REWARDED_AD_MODE || 'off';
const enabled = mode === 'test' || mode === 'live';
const adUnitPath = mode === 'test'
  ? TEST_REWARDED_AD_UNIT
  : (import.meta.env.VITE_REWARDED_AD_UNIT || DOWNLOAD_REWARDED_AD_UNIT);

const failureMessages = {
  empty: '目前沒有廣告，可直接下載。',
  unsupported: '無法播放廣告，可直接下載。',
  timeout: '廣告載入逾時，可直接下載。',
  busy: '廣告處理中，請稍後再試。',
};

export default function RewardedDownloadButton({ url, getPlayer }) {
  const [state, setState] = useState('idle');
  const [failure, setFailure] = useState('');
  const [open, setOpen] = useState(false);
  const disposeRef = useRef(null);
  const requestingRef = useRef(false);
  const frameName = useId();
  const downloadUrl = recordingDownloadUrl(url, API_BASE_URL);
  // The attachment response downloads in the browser without leaving this page.
  const downloadFrame = <iframe name={frameName} title="錄音下載" hidden />;

  useEffect(() => () => {
    disposeRef.current?.();
  }, []);

  function close() {
    disposeRef.current?.();
    disposeRef.current = null;
    requestingRef.current = false;
    setOpen(false);
    setState('idle');
  }

  function watchAd() {
    if (requestingRef.current) return;
    requestingRef.current = true;
    setState('loading');
    disposeRef.current = requestRewardedAd({
      adUnitPath,
      onReady: (show) => {
        getPlayer?.()?.pause();
        setOpen(false);
        setState('playing');
        show();
      },
      onResult: (result) => {
        requestingRef.current = false;
        disposeRef.current = null;
        if (result === 'earned') {
          setState('earned');
        } else if (result === 'cancelled' || result === 'busy') {
          setState('cancelled');
          setFailure(result === 'busy' ? failureMessages.busy : '廣告尚未完成，請重試。');
        } else {
          setState('unavailable');
          setFailure(failureMessages[result] || '廣告載入失敗，可直接下載。');
        }
        setOpen(true);
      },
    });
  }

  if (!enabled) {
    return <>{downloadFrame}<Button variant="outlined" href={downloadUrl} target={frameName} disabled={!downloadUrl}>下載錄音檔</Button></>;
  }

  const canDownload = state === 'earned' || state === 'unavailable';
  return (
    <>
      {downloadFrame}
      <Button
        variant="outlined"
        disabled={!downloadUrl || state === 'playing'}
        onClick={() => { setState('consent'); setFailure(''); setOpen(true); }}
      >
        下載錄音檔
      </Button>
      <Dialog open={open} onClose={close} aria-labelledby="rewarded-download-title" maxWidth="xs" fullWidth>
        <DialogTitle id="rewarded-download-title">
          下載錄音檔
        </DialogTitle>
        <DialogContent>
          {mode === 'test' && <Alert severity="info" sx={{ mb: 2 }}>測試廣告</Alert>}
          {state === 'consent' && (
            <DialogContentText>看一則廣告即可下載。</DialogContentText>
          )}
          {state === 'loading' && (
            <DialogContentText role="status"><CircularProgress size={18} sx={{ mr: 1 }} />廣告載入中…</DialogContentText>
          )}
          {state === 'earned' && <DialogContentText>廣告已完成，可以下載了。</DialogContentText>}
          {(state === 'unavailable' || state === 'cancelled') && <Alert severity="info">{failure}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{canDownload ? '關閉' : '取消'}</Button>
          {state === 'consent' && <Button variant="contained" onClick={watchAd}>觀看廣告</Button>}
          {state === 'cancelled' && <Button onClick={watchAd}>重試</Button>}
          {canDownload && <Button variant="contained" href={downloadUrl} target={frameName} onClick={close}>下載</Button>}
        </DialogActions>
      </Dialog>
    </>
  );
}
